import graphql from "babel-plugin-relay/macro";
import { useFragment } from "react-relay";

import {
  ChargePaymentStatus,
  useJoinableSpots_Booking$key,
} from "./__generated__/useJoinableSpots_Booking.graphql";

const acceptableChargeStates: ChargePaymentStatus[] = [
  "UNPAID",
  "NO_CHARGES",
  "AUTO_CHARGE",
];

export const useJoinableSpots = (
  bookingRef: useJoinableSpots_Booking$key | null
) => {
  const data = useFragment(useJoinableSpotsFragmentString, bookingRef);
  const hasMemberRounds =
    data?.rounds?.edges?.some(
      (edge) => edge?.node?.__typename === "MemberRound"
    ) ?? false;

  const requiresPlayerCounts = data?.startSlot?.requirePlayerCounts;
  const isDurationFacility =
    data?.startSlot?.agenda?.facility?.type !== "GOLF_COURSE";
  const hasAvailableCapacity = (data?.startSlot?.availableCapacity ?? 0) > 0;

  const isNotYetPaid =
    !!acceptableChargeStates.includes(data?.paymentStatus ?? "PAID") &&
    !!data?.rounds?.edges?.every(
      (edge) =>
        edge?.node?.__typename === "CustomerRound" &&
        acceptableChargeStates.includes(edge?.node?.paymentStatus ?? "PAID")
    );

  const durationCountExemptHasJoinableRounds =
    !hasMemberRounds &&
    isDurationFacility &&
    hasAvailableCapacity &&
    isNotYetPaid &&
    !requiresPlayerCounts;

  const joinableRoundCount = hasMemberRounds
    ? data?.rounds?.edges?.filter(
        (round) =>
          round?.node?.__typename === "MemberRound" && !round?.node?.player?.id
      ).length ?? 0
    : durationCountExemptHasJoinableRounds
    ? data?.startSlot?.availableCapacity ?? 0
    : data?.rounds?.edges?.filter((round) => !round?.node?.player?.id).length ??
      0;

  const hasJoinableRounds = joinableRoundCount > 0;

  return {
    hasJoinableSpots: hasJoinableRounds,
    joinableSpotCount: joinableRoundCount,
  };
};

const useJoinableSpotsFragmentString = graphql`
  fragment useJoinableSpots_Booking on Booking {
    ... on StandardBooking {
      paymentStatus
    }
    rounds {
      edges {
        node {
          __typename
          player {
            id
          }
          paymentStatus
        }
      }
    }
    startSlot {
      agenda {
        facility {
          type
        }
      }
      ... on TimeSlot {
        requirePlayerCounts
        availableCapacity(bookingType: STANDARD)
      }
    }
  }
`;
